import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Star } from 'lucide-react';
import Header from '../component/Header';
import Footer from '../component/Footer';
import { getPlans, Plan } from '../services/planService';

type StaticPricingPlan = {
  key: string;
  displayName: string;
  planName: string;
  price: number;
  description: string;
  features: string[];
  highlighted?: boolean;
};

type PricingPlan = StaticPricingPlan & {
  id: number | null;
  isActive: boolean;
};

export const STATIC_PRICING_PLANS: StaticPricingPlan[] = [
  {
    key: 'basic',
    displayName: 'Basic',
    planName: 'Basic Monthly',
    price: 29,
    description: 'Access to Basic Courses, Access on any device, Cancel Anytime',
    features: [
      'Access to Basic Courses',
      'Access on any device',
      'Cancel Anytime',
    ],
  },
  {
    key: 'premium',
    displayName: 'Premium',
    planName: 'Pro Monthly',
    price: 99,
    description: 'Access to All Courses, Access On Any Device, Unlimited Downloads, Unlimited Transcripts, Cancel Anytime',
    features: [
      'Access to All Courses',
      'Access On Any Device',
      'Unlimited Downloads',
      'Unlimited Transcripts',
      'Cancel Anytime',
    ],
    highlighted: true,
  },
  {
    key: 'standard',
    displayName: 'Standard',
    planName: 'Standard Monthly',
    price: 59,
    description: 'Access to Advanced Courses, Access on any device, Limited Downloads, Limited Transcripts, Cancel Anytime',
    features: [
      'Access to Advanced Courses',
      'Access on any device',
      'Limited Downloads',
      'Limited Transcripts',
      'Cancel Anytime',
    ],
  },
];

const normalizePlanName = (name: string) =>
  name.toLowerCase().replace(/\s+monthly$/, '').replace(/^pro$/, 'premium');

const getStaticPlanForDbPlan = (plan: Plan) => {
  const normalizedName = normalizePlanName(plan.name);

  return STATIC_PRICING_PLANS.find((staticPlan) =>
    [staticPlan.key, staticPlan.displayName, staticPlan.planName]
      .map(normalizePlanName)
      .includes(normalizedName),
  );
};

const mapDbPlanToPricingPlan = (plan: Plan): PricingPlan => {
  const staticPlan = getStaticPlanForDbPlan(plan);
  const fallbackFeatures = plan.description
    ? plan.description.split(',').map((item) => item.trim()).filter(Boolean)
    : ['Cancel Anytime'];

  return {
    key: staticPlan?.key || normalizePlanName(plan.name).replace(/\s+/g, '-'),
    displayName: staticPlan?.displayName || plan.name.replace(/\s+Monthly$/, ''),
    planName: plan.name,
    price: Number(plan.price),
    description: plan.description || staticPlan?.description || '',
    features: staticPlan?.features || fallbackFeatures,
    highlighted: staticPlan?.highlighted,
    id: plan.id,
    isActive: plan.is_active,
  };
};

const staticPlansForFallback = (): PricingPlan[] =>
  STATIC_PRICING_PLANS.map((plan) => ({
    ...plan,
    id: null,
    isActive: false,
  }));

const sortPlans = (plans: PricingPlan[]) => {
  const order = new Map(STATIC_PRICING_PLANS.map((plan, index) => [plan.key, index]));

  return [...plans].sort((left, right) => {
    const leftOrder = order.get(left.key) ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = order.get(right.key) ?? Number.MAX_SAFE_INTEGER;

    if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    return left.price - right.price;
  });
};

const Pricing = () => {
  const navigate = useNavigate();
  const ctaText = 'BUY NOW';
  const [plans, setPlans] = useState<PricingPlan[]>(staticPlansForFallback);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadPlans = async () => {
      try {
        const response = await getPlans();
        const activePlans = (response.data || [])
          .filter((plan) => plan.is_active)
          .map(mapDbPlanToPricingPlan);

        if (!isMounted) return;

        if (activePlans.length > 0) {
          setPlans(sortPlans(activePlans));
        } else {
          setPlans(staticPlansForFallback());
        }
      } catch {
        if (!isMounted) return;

        setPlans(staticPlansForFallback());
      } finally {
        if (isMounted) {
          setIsLoadingPlans(false);
        }
      }
    };

    loadPlans();

    return () => {
      isMounted = false;
    };
  }, []);

  const navigateToCheckout = (plan: PricingPlan) => {
    navigate('/checkout', {
      state: {
        plan_id: plan.id,
        plan_key: plan.key,
        plan_name: plan.planName,
        price: plan.price,
      },
    });
  };

  const handlePurchase = (plan: PricingPlan) => {
    navigateToCheckout(plan);
  };

  return (
    <>
      <Header />

      <main className="font-sans text-zinc-900 ">
        <section className="relative overflow-hidden bg-white pt-24 md:pt-32 pb-24">
          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage: 'linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          ></div>

          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white pointer-events-none"></div>

          <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12 text-center mb-16 md:mb-24">
            <h1 className="mb-6 text-5xl font-black tracking-tighter text-zinc-950 md:text-6xl">
              Simple, transparent pricing
            </h1>

            <p className="mx-auto max-w-2xl text-lg font-medium text-zinc-600 leading-relaxed">
              Choose the plan that best fits your learning goals. Upgrade, downgrade, or cancel anytime.
            </p>
          </div>

          <div className="relative z-10 mx-auto grid max-w-7xl gap-8 px-6 lg:grid-cols-3 lg:px-12 items-center mb-32">
            {plans.map((plan) => {
              const isHighlighted = Boolean(plan.highlighted);
              const cardClassName = isHighlighted
                ? 'relative rounded-[2rem] border-2 border-[#f59e0b] bg-zinc-950 p-8 md:p-12 shadow-[0_20px_50px_rgba(245,158,11,0.2)] transform md:-translate-y-4 z-10 flex flex-col h-full text-center'
                : 'rounded-[2rem] border border-zinc-200 bg-white p-8 md:p-10 shadow-xl transition-transform hover:-translate-y-1 duration-300 flex flex-col h-full text-center';
              const featureIconClassName = isHighlighted
                ? 'h-5 w-5 text-[#f59e0b] shrink-0'
                : 'h-5 w-5 text-zinc-900 shrink-0';
              const buttonClassName = isHighlighted
                ? 'block min-h-12 rounded-xl bg-[#f59e0b] px-5 py-4 text-center text-sm font-black uppercase tracking-widest text-zinc-950 transition-all shadow-[0_0_20px_rgba(163,230,53,0.3)] hover:-translate-y-1 mt-auto disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0'
                : 'hover:bg-white hover:text-zinc-950 hover:border-[#09090B] border border-[#09090B] block min-h-12 rounded-xl bg-[#09090B] text-white px-5 py-4 text-center text-sm font-black uppercase tracking-widest transition-all shadow-md hover:shadow-lg mt-auto disabled:cursor-not-allowed disabled:opacity-60';

              return (
                <article key={plan.key} className={cardClassName}>
                  {isHighlighted && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-[#f59e0b] px-4 py-1.5 text-xs font-black uppercase tracking-widest text-zinc-950 shadow-md flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 fill-zinc-950" /> Most Popular
                    </div>
                  )}

                  <h3 className={`${isHighlighted ? 'text-2xl text-[#f59e0b]' : 'text-xl text-zinc-950'} font-black mb-6`}>
                    {plan.displayName}
                  </h3>

                  <h2 className={`${isHighlighted ? 'mb-8 text-6xl text-white border-zinc-800' : 'mb-8 text-5xl text-zinc-950 border-zinc-100'} font-black flex items-end justify-center gap-1 border-b pb-8`}>
                    &euro;{plan.price}
                    <span className={`${isHighlighted ? 'font-medium text-zinc-400' : 'font-bold text-zinc-500'} text-sm mb-2 tracking-normal`}>
                      /Per Month
                    </span>
                  </h2>

                  <ul className={`${isHighlighted ? 'text-white' : 'text-zinc-600'} mb-10 space-y-5 text-sm font-bold flex-1 text-left`}>
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3">
                        <CheckCircle2 className={featureIconClassName} /> {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    onClick={() => handlePurchase(plan)}
                    disabled={isLoadingPlans}
                    className={buttonClassName}
                  >
                    {isLoadingPlans ? 'LOADING...' : ctaText}
                  </button>
                </article>
              );
            })}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Pricing;
